import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeftRight, BarChart3, Bell, Building2, CalendarDays, CheckCircle2, ChevronRight, Clock, Hourglass, PlayCircle, Plus, Rocket, User, Users } from "lucide-react";
import { api } from "@/lib/api";
import { isManagerRole, useAuth } from "@/lib/auth";
import { usePendingApprovals } from "@/lib/pending";
import { Card, Overline } from "@/components/app/Primitives";
import { PriorityBadge, StatusBadge } from "@/components/app/Badges";

function greeting(h = new Date().getHours()) {
  if (h < 5) return "Working late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}
const cap = (r) => (r ? r.charAt(0).toUpperCase() + r.slice(1) : "");

function Nudge({ tone, icon: Icon, title, sub, onClick, testID }) {
  const border = tone === "danger" ? "border-a-danger" : "border-a-warning";
  const ic = tone === "danger" ? "bg-a-danger/15 text-a-danger" : "bg-a-warning/15 text-a-warning";
  return (
    <Card onClick={onClick} testID={testID} className={`!border-[1.5px] ${border}`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${ic}`}><Icon size={20} /></span>
        <div className="flex-1">
          <p className="text-[15px] font-extrabold text-a-text">{title}</p>
          <p className="text-xs text-a-muted">{sub}</p>
        </div>
        <ChevronRight size={20} className="text-a-muted" />
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, activeWorkspace, workspaces } = useAuth();
  const { count: pendingCount } = usePendingApprovals();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activeWorkspace) return;
    try { setSummary(await api("/dashboard/summary", { query: { workspace_id: activeWorkspace.id } })); }
    catch (e) { console.log("dashboard load", e); }
    finally { setLoading(false); }
  }, [activeWorkspace]);
  useEffect(() => { load(); }, [load]);

  const firstName = user?.full_name?.split(" ")[0] || "there";
  const bellBadge = (summary?.unread_notifications ?? 0) + pendingCount;
  const hello = useMemo(() => greeting(), []);
  const isManager = isManagerRole(activeWorkspace?.role);
  const c = summary?.counts || {};
  const approvals = summary?.manager?.approvals_pending ?? 0;
  const noTasksYet = !loading && (c.total_tasks ?? 0) === 0;
  const prod = summary?.productivity ?? 0;

  const kpi = (label, value, Icon, color, testID, to) => (
    <Card key={testID} onClick={() => navigate(to)} testID={testID} className="flex flex-col gap-1.5 !p-3.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-[10px]" style={{ backgroundColor: `rgb(var(${color}) / 0.14)`, color: `rgb(var(${color}))` }}><Icon size={18} /></span>
      <p className="text-2xl font-extrabold tracking-tight text-a-text">{value ?? 0}</p>
      <p className="text-xs font-semibold text-a-muted">{label}</p>
    </Card>
  );

  return (
    <div className="page-in space-y-3.5 px-4 pt-4 md:pt-8" data-testid="dashboard-page">
      <div className="flex items-center gap-2 pb-0.5 pt-2">
        <div className="min-w-0 flex-1">
          <Overline>{hello}</Overline>
          <h1 className="truncate text-[28px] font-extrabold tracking-tight text-a-text" data-testid="dashboard-name">{firstName}</h1>
        </div>
        <button onClick={() => navigate("/notifications")} data-testid="dashboard-bell" className="relative flex h-10 w-10 items-center justify-center rounded-full border border-a-border bg-a-surface text-a-text hover:border-a-primary/50">
          <Bell size={20} />
          {bellBadge ? <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-a-danger px-1 text-[10px] font-extrabold text-white">{bellBadge > 9 ? "9+" : bellBadge}</span> : null}
        </button>
      </div>

      <button onClick={() => navigate("/workspaces")} data-testid="dashboard-workspace-chip" className="card-hover flex w-full items-center gap-2.5 rounded-[14px] border border-a-border bg-a-surface px-3 py-2.5 text-left">
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-a-dim text-a-primary">{activeWorkspace?.type === "company" ? <Building2 size={13} /> : <User size={13} />}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-extrabold text-a-text">{activeWorkspace?.name || "Choose workspace"}</p>
          <p className="truncate text-[11px] text-a-muted">{activeWorkspace?.role ? `${cap(activeWorkspace.role)} · ` : ""}{workspaces.length > 1 ? `${workspaces.length} workspaces · tap to switch` : "tap to manage"}</p>
        </div>
        <ArrowLeftRight size={16} className="text-a-muted" />
      </button>

      {approvals > 0 ? <Nudge testID="nudge-approvals" tone="warning" icon={AlertCircle} title={`${approvals} task${approvals > 1 ? "s" : ""} awaiting your approval`} sub="Tap to review submissions" onClick={() => navigate("/tasks?tab=approvals")} /> : null}
      {(c.overdue ?? 0) > 0 ? <Nudge testID="nudge-overdue" tone="danger" icon={Clock} title={`${c.overdue} overdue task${c.overdue > 1 ? "s" : ""}`} sub="Get back on track" onClick={() => navigate("/tasks?tab=overdue")} /> : null}

      {noTasksYet ? (
        <Card className="!border-[1.5px] !border-a-primary" testID="dashboard-empty">
          <div className="flex flex-col items-center gap-2.5 p-2 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-a-dim text-a-primary"><Rocket size={26} /></span>
            <p className="text-[17px] font-extrabold text-a-text">Ready to move things forward?</p>
            <p className="text-[13px] text-a-muted">{isManager ? "Create your first task and assign it to a teammate." : "Create a task or wait for your manager to assign one."}</p>
            <button onClick={() => navigate("/task/new")} data-testid="dashboard-create-first" className="btn-press mt-0.5 flex items-center gap-2 rounded-xl bg-a-primary px-[18px] py-3 text-sm font-extrabold text-white hover:brightness-110"><Plus size={18} /> Create first task</button>
          </div>
        </Card>
      ) : (
        <>
          <Card testID="dashboard-productivity">
            <Overline>Productivity</Overline>
            <div className="mt-1.5 flex items-end justify-between">
              <p className="text-[40px] font-extrabold leading-none tracking-tighter text-a-text">{prod}%</p>
              <p className="mb-1 text-xs text-a-muted">{c.completed ?? 0} of {c.total_tasks ?? 0} tasks complete</p>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-lg bg-a-border"><div className="h-2 rounded-lg bg-a-primary transition-[width] duration-700" style={{ width: `${prod}%` }} /></div>
            <div className="mt-3 flex flex-wrap gap-4">
              {[["bg-a-danger", `Overdue ${c.overdue ?? 0}`], ["bg-a-warning", `High ${c.high_priority ?? 0}`], ["bg-a-primary", `Approvals ${c.waiting_approval ?? 0}`]].map(([d, l]) => (
                <span key={l} className="flex items-center gap-1.5 text-xs text-a-muted"><span className={`h-2 w-2 rounded-full ${d}`} />{l}</span>
              ))}
            </div>
          </Card>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {kpi("Due Today", c.due_today, CalendarDays, "--a-primary", "kpi-due-today", "/tasks?tab=today")}
            {kpi("Pending", c.pending, Hourglass, "--a-warning", "kpi-pending", "/tasks?tab=pending")}
            {kpi("In Progress", c.in_progress, PlayCircle, "--a-primary", "kpi-in-progress", "/tasks?tab=in_progress")}
            {kpi("Completed", c.completed, CheckCircle2, "--a-success", "kpi-completed", "/tasks?tab=completed")}
          </div>
          {summary?.manager ? (
            <Card testID="dashboard-team-overview">
              <Overline>Team overview</Overline>
              <div className="mt-2.5 grid grid-cols-4">
                {[[summary.manager.approvals_pending, "Approvals", "text-a-text"], [summary.manager.team_overdue, "Overdue", "text-a-danger"], [summary.manager.team_completed, "Team done", "text-a-success"], [summary.manager.team_total, "Team tasks", "text-a-text"]].map(([v, l, tc]) => (
                  <div key={l} className="py-1"><p className={`text-[22px] font-extrabold tracking-tight ${tc}`}>{v}</p><p className="text-[11px] font-semibold text-a-muted">{l}</p></div>
                ))}
              </div>
            </Card>
          ) : null}
        </>
      )}

      <div className="flex gap-2.5">
        <button onClick={() => navigate("/task/new")} data-testid="quick-new-task" className="btn-press flex flex-1 items-center justify-center gap-2 rounded-xl bg-a-primary py-3 text-sm font-bold text-white hover:brightness-110"><Plus size={18} /> New task</button>
        <button onClick={() => navigate("/team")} data-testid="quick-team" className="btn-press flex flex-1 items-center justify-center gap-2 rounded-xl border border-a-border bg-a-surface py-3 text-sm font-bold text-a-text hover:border-a-primary/50"><Users size={18} /> Team</button>
        <button onClick={() => navigate("/reports")} data-testid="quick-reports" className="btn-press flex flex-1 items-center justify-center gap-2 rounded-xl border border-a-border bg-a-surface py-3 text-sm font-bold text-a-text hover:border-a-primary/50"><BarChart3 size={18} /> Reports</button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-a-text">Recent activity</h2>
          <button onClick={() => navigate("/tasks")} className="font-bold text-a-primary hover:underline" data-testid="dashboard-view-all">View all</button>
        </div>
        {loading ? (
          <Card><p className="text-a-muted">Loading…</p></Card>
        ) : (summary?.recent || []).length === 0 ? (
          !noTasksYet ? <Card><p className="text-a-muted">Nothing recent. New activity will show up here.</p></Card> : null
        ) : (
          summary.recent.map((t) => (
            <Card key={t.id} onClick={() => navigate(`/task/${t.id}`)} testID={`recent-task-${t.id}`}>
              <div className="space-y-2">
                <div className="flex justify-between gap-2"><p className="line-clamp-2 flex-1 font-bold text-a-text">{t.title}</p><PriorityBadge priority={t.priority} /></div>
                <div className="flex items-center justify-between"><StatusBadge status={t.status} /><span className="text-xs text-a-muted">{t.progress ?? 0}%</span></div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
